import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { StageBadge, StatusBadge } from "@/components/StageBadge";
import Timeline from "@/components/Timeline";
import DealDrawer from "@/components/DealDrawer";
import FileUploader, { FileGrid } from "@/components/FileUploader";
import Pager from "@/components/Pager";
import RejectedLeadsArchive from "@/components/RejectedLeadsArchive";
import { fmtDay } from "@/lib/meta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Search, Loader2, Building2, RotateCcw, MapPin } from "lucide-react";
import { toast } from "sonner";

export default function Properties() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const LIMIT = 50;
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit: LIMIT, skip }; if (search) params.search = search; if (statusFilter) params.status = statusFilter;
    api.get("/properties", { params }).then(({ data }) => { setRows(data.items); setTotal(data.total); }).finally(() => setLoading(false));
  }, [search, statusFilter, skip]);
  useEffect(() => { setSkip(0); }, [search, statusFilter]);
  useEffect(() => { load(); }, [load]);

  const STATUSES = ["", "active_deal", "dormant", "rejected_archive", "live_hostel"];

  return (
    <div>
      <PageHeader title="Properties" subtitle="Every property, with its full deal history. Never deleted.">
        <div className="relative w-72">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <Input placeholder="Search name, city, phone…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8" data-testid="properties-search" />
        </div>
      </PageHeader>

      <div className="px-8 py-6">
        <div className="flex gap-1.5 mb-4">
          {STATUSES.map((s) => (
            <button key={s || "all"} onClick={() => setStatusFilter(s)} data-testid={`prop-filter-${s || "all"}`}
              className={`text-xs px-2.5 py-1 rounded border transition-colors ${statusFilter === s ? "border-[#0047FF] bg-blue-50 text-[#0047FF]" : "border-gray-200 text-gray-600"}`}>
              {s ? s.replace(/_/g, " ") : "All"}
            </button>
          ))}
        </div>

        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#0047FF]" /></div>
          ) : rows.length === 0 ? (
            <div className="text-center py-16 text-gray-400 text-sm" data-testid="properties-empty">{statusFilter === "rejected_archive" ? "No rejected properties." : "No properties yet. Promote a lead to create one."}</div>
          ) : (
            <Table>
              <TableHeader><TableRow className="bg-gray-50">
                <TableHead>Property</TableHead><TableHead>City</TableHead><TableHead>Rooms</TableHead>
                <TableHead>Status</TableHead><TableHead>Deals</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((p) => (
                  <TableRow key={p.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setOpenId(p.id)} data-testid={`property-row-${p.id}`}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell>{p.city || "—"}</TableCell>
                    <TableCell>{p.num_rooms || "—"}</TableCell>
                    <TableCell><StatusBadge status={p.status} /></TableCell>
                    <TableCell className="text-sm text-gray-600">{p.open_deals} open / {p.total_deals} total</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
        <div className="flex items-center justify-between mt-3">
          <div className="text-xs text-gray-400">{total} propert{total === 1 ? "y" : "ies"}</div>
          <Pager skip={skip} limit={LIMIT} total={total} onPrev={() => setSkip(Math.max(0, skip - LIMIT))} onNext={() => setSkip(skip + LIMIT)} />
        </div>
        {statusFilter === "rejected_archive" && <RejectedLeadsArchive />}
      </div>

      <PropertyDrawer pid={openId} open={!!openId} onOpenChange={(v) => !v && setOpenId(null)} onChanged={load} />
    </div>
  );
}

function PropertyDrawer({ pid, open, onOpenChange, onChanged }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dealId, setDealId] = useState(null);
  const [dealOpen, setDealOpen] = useState(false);

  const load = () => { if (!pid) return; setLoading(true); api.get(`/properties/${pid}`).then(({ data }) => setData(data)).finally(() => setLoading(false)); };
  useEffect(() => { if (open && pid) load(); }, [open, pid]);

  const p = data?.property;
  const reopen = async () => { try { await api.post(`/properties/${pid}/reopen`); load(); onChanged?.(); toast.success("New deal opened"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const toggleLive = async (v) => { try { await api.put(`/properties/${pid}/live-hostel`, { live_hostel: v }); load(); onChanged?.(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const addPhoto = async (path) => { await api.post(`/properties/${pid}/photos`, { path }); load(); };
  const addPlan = async (path) => { await api.post(`/properties/${pid}/floor-plans`, { path }); load(); };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[720px] p-0 overflow-y-auto bg-white" data-testid="property-drawer">
        {loading || !p ? (
          <div className="h-full flex items-center justify-center py-20">
            <SheetTitle className="sr-only">Property</SheetTitle>
            <Loader2 className="h-6 w-6 animate-spin text-[#0047FF]" />
          </div>
        ) : (
          <div>
            <SheetHeader className="px-6 py-5 border-b border-gray-200">
              <SheetTitle className="font-heading text-xl flex items-center gap-2"><Building2 className="h-5 w-5 text-[#0047FF]" /> {p.name}</SheetTitle>
              <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{p.city || "—"}</span>
                <span>{p.num_rooms || "—"} rooms</span>
                <StatusBadge status={p.status} />
              </div>
              <div className="flex items-center gap-3 mt-3">
                <Button size="sm" variant="outline" onClick={reopen} data-testid="reopen-btn"><RotateCcw className="h-4 w-4" /> Reopen (new deal)</Button>
                {user?.role === "admin" && (
                  <label className="flex items-center gap-2 text-sm text-gray-600">
                    <Switch checked={!!p.live_hostel} onCheckedChange={toggleLive} data-testid="live-hostel-switch" /> Live hostel
                  </label>
                )}
              </div>
            </SheetHeader>

            <Tabs defaultValue="deals" className="px-6 py-4">
              <TabsList className="bg-transparent border-b border-gray-200 rounded-none h-auto p-0 gap-4 w-full justify-start">
                <TabsTrigger value="deals" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0047FF] data-[state=active]:bg-transparent px-1 pb-2" data-testid="ptab-deals">Deals ({data.deals.length})</TabsTrigger>
                <TabsTrigger value="timeline" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0047FF] data-[state=active]:bg-transparent px-1 pb-2" data-testid="ptab-timeline">Timeline</TabsTrigger>
                <TabsTrigger value="files" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0047FF] data-[state=active]:bg-transparent px-1 pb-2" data-testid="ptab-files">Photos & Plans</TabsTrigger>
              </TabsList>

              <TabsContent value="deals" className="pt-4 space-y-2">
                {data.deals.length === 0 ? <div className="text-sm text-gray-400 py-6 text-center">No deals.</div> :
                  data.deals.map((d) => (
                    <div key={d.id} onClick={() => { setDealId(d.id); setDealOpen(true); }}
                      className="border border-gray-200 rounded-md p-3 flex items-center justify-between cursor-pointer hover:border-[#0047FF]" data-testid={`prop-deal-${d.id}`}>
                      <div>
                        <div className="flex items-center gap-2"><StageBadge stage={d.stage} /><span className="text-xs text-gray-500 capitalize">{d.status}</span></div>
                        <div className="text-xs text-gray-400 mt-1">Rep: {d.rep_name || "—"} · {fmtDay(d.created_at)}{d.rejection_reason ? ` · Rejected: ${(d.rejection_reason.code || "").replace(/_/g, " ")}` : ""}</div>
                      </div>
                      <span className="text-xs text-gray-400">{d.days_in_stage}d in stage</span>
                    </div>
                  ))}
              </TabsContent>

              <TabsContent value="timeline" className="pt-4"><Timeline events={data.events} /></TabsContent>

              <TabsContent value="files" className="pt-4 space-y-5">
                <div>
                  <div className="flex items-center justify-between mb-2"><Label>Photos</Label><FileUploader label="Add photo" onUploaded={addPhoto} testid="photo-uploader" /></div>
                  <FileGrid files={p.photos} />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2"><Label>Floor plans</Label><FileUploader label="Add floor plan" onUploaded={addPlan} testid="plan-uploader" /></div>
                  <FileGrid files={p.floor_plans} />
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}
        <DealDrawer dealId={dealId} open={dealOpen} onOpenChange={setDealOpen} onChanged={() => { load(); onChanged?.(); }} />
      </SheetContent>
    </Sheet>
  );
}
